import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Heart, Mail } from "lucide-react";
import GoogleAuthHelper from "@/components/GoogleAuthHelper";

export default function Login() {
  const { loginWithPassword } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [magicBusy, setMagicBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await loginWithPassword(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  const sendMagicLink = async () => {
    if (!email) { toast.error("Please enter your email above first"); return; }
    setMagicBusy(true);
    try {
      const ref = (() => { try { return sessionStorage.getItem("hc_ref") || undefined; } catch (e) { return undefined; } })();
      await api.post("/auth/magic-link/request", { email, app_origin: window.location.origin, ...(ref ? { ref } : {}) });
      setMagicSent(true);
      setCooldown(30);
      const iv = setInterval(() => setCooldown((c) => { if (c <= 1) { clearInterval(iv); return 0; } return c - 1; }), 1000);
      toast.success("Check your email for a sign-in link (check spam too)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send link");
    } finally { setMagicBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-[#FCFAF8] relative overflow-hidden">
      <div className="hc-blob" style={{ background: "#D8E2D8", width: 400, height: 400, top: -100, right: -100 }} />
      <div className="hc-blob" style={{ background: "#E8DCC8", width: 360, height: 360, bottom: -100, left: -100 }} />

      <div className="relative z-10 w-full max-w-md mx-auto px-6 py-12 flex flex-col justify-center">
        <Link to="/" className="flex items-center gap-2 mb-12" data-testid="login-back-home">
          <img src="/hope-chest-logo.png" alt="Hope Chest" className="w-10 h-10 object-contain" />
          <span className="font-brand text-xl font-semibold">Hope Chest</span>
        </Link>
        <h1 className="font-display text-3xl md:text-4xl font-semibold mb-2">Welcome back.</h1>
        <p className="text-[#7A736E] mb-8">Sign in to your Hope Chest.</p>

        <Button
          onClick={googleSignIn}
          variant="outline"
          className="w-full rounded-full bg-white border-[#EBE5DF] hover:bg-[#F3EFEA] py-6 mb-4"
          data-testid="login-google-button"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </Button>
        <GoogleAuthHelper />

        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-[#EBE5DF]" />
          <span className="text-xs text-[#7A736E] uppercase tracking-wider">or with email</span>
          <div className="flex-1 h-px bg-[#EBE5DF]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-sm text-[#3D3935]">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 rounded-xl border-[#EBE5DF] py-6"
              placeholder="you@example.com"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-sm text-[#3D3935]">Password</Label>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 rounded-xl border-[#EBE5DF] py-6"
              placeholder="••••••••"
              data-testid="login-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] py-6"
            data-testid="login-submit-button"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="mt-6 p-5 rounded-2xl bg-[#F3EFEA] border border-[#EBE5DF]">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-2xl bg-[#D8E2D8] flex items-center justify-center flex-shrink-0 mt-0.5">
              <Mail className="w-4 h-4 text-[#3A5A4A]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">Forgot your password? Skip it.</p>
              <p className="text-xs text-[#7A736E] mt-1">We'll email you a one-tap sign-in link.</p>
              <Button
                onClick={sendMagicLink}
                disabled={magicBusy || cooldown > 0}
                variant="ghost"
                size="sm"
                className="mt-2 rounded-full text-[#3A5A4A] hover:bg-white px-3 -ml-2"
                data-testid="login-magic-link-button"
              >
                {cooldown > 0 ? `Link sent — resend in ${cooldown}s` : magicBusy ? "Sending…" : magicSent ? "Resend sign-in link →" : "Email me a sign-in link →"}
              </Button>
              {magicSent && (
                <p className="text-[11px] text-[#7A736E] mt-1.5" data-testid="magic-sent-hint">
                  Didn't get it? Check your spam or junk folder, then tap resend.
                </p>
              )}
            </div>
          </div>
        </div>

        <p className="mt-8 text-sm text-center text-[#7A736E]">
          New here? <Link to="/signup" className="text-[#3A5A4A] font-medium hover:underline" data-testid="login-to-signup">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
